import { useMemo, useState, useCallback, type JSX } from "react";
import clsx from "clsx";
import { Collapse, Tooltip } from "@/shared/ui";
import { type ITableData } from "@/service/assessment";

export type TableSelection = { id: string; value: string | null };

interface AssessmentTableProps {
  data: ITableData;
  selections?: TableSelection[];
  onChange: (next: TableSelection[]) => void;
  className?: string;
}

function buildSelectionMap(selections: TableSelection[]): Map<string, string | null> {
  const map = new Map<string, string | null>();

  for (const s of selections) map.set(s.id, s.value);

  return map;
}

export function AssessmentTable(props: AssessmentTableProps): JSX.Element {
  const { data, selections, onChange, className } = props;

  const selectionByRow = useMemo(() => buildSelectionMap(selections ?? []), [selections]);

  const handleSelectOption = useCallback(
    (rowId: string, optionId: string) => {
      const next = data.rows.map((r) => ({
        id: r.id,
        value: r.id === rowId ? optionId : (selectionByRow.get(r.id) ?? null),
      }));
      onChange(next);
    },
    [data.rows, onChange, selectionByRow],
  );

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const handleToggleRow = useCallback((rowId: string) => {
    setExpandedId((prev) => (prev === rowId ? null : rowId));
  }, []);

  const colsCount = data.columns.length;
  const tableStyle = { ["--cols" as any]: colsCount } as React.CSSProperties;

  return (
    <div className={clsx("grid gap-0.5 px-10", className)} style={tableStyle}>
      {/* ClientHeader */}
      <div className="grid [grid-template-columns:1fr_repeat(var(--cols),minmax(114px,72px))] items-stretch">
        <div className="sticky left-0 z-[1] flex flex-col items-start px-8 py-6 whitespace-pre-line" />
        {data.columns.map((c) => (
          <div key={c.id} className="flex items-start justify-center p-8 whitespace-nowrap">
            <div className="text-eerie-black font-family-third text-center text-base font-bold">{c.name}</div>
          </div>
        ))}
      </div>
      {/* Rows */}
      {data.rows.map((row, rowIndex) => {
        const selectedValue = selectionByRow.get(row.id) ?? null;
        const isOpen = expandedId === row.id;

        return (
          <div
            key={row.id}
            className={clsx(
              "bg-background-grey overflow-hidden",
              rowIndex === 0 && "rounded-t-lg",
              rowIndex === data.rows.length - 1 && "rounded-b-lg",
            )}
          >
            <div className="grid [grid-template-columns:1fr_repeat(var(--cols),minmax(114px,72px))] items-stretch">
              {/* Sticky left cell */}
              <div className="bg-background-grey sticky left-0 z-[1] flex flex-col items-start px-8 py-6 whitespace-pre-line">
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-left"
                  onClick={() => handleToggleRow(row.id)}
                >
                  <span className="text-eerie-black font-family-third text-base font-bold">{row.title}</span>
                  <svg
                    className={clsx(
                      "text-sonic-silver h-5 w-5 shrink-0 transition-transform duration-150 ease-in-out",
                      isOpen && "rotate-90",
                    )}
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M7.5 15.5L12.5 10.5L7.5 5.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <Collapse open={Boolean(isOpen && row.description)}>
                  {row.description ? (
                    <div className="text-eerie-black font-family-third mt-3 block text-left text-xs">
                      {row.description}
                    </div>
                  ) : null}
                </Collapse>
              </div>
              {/* Options */}
              {data.columns.map((col) => {
                const optionDesc = row.options.find((o) => o.id === col.id)?.description;

                return (
                  <Tooltip
                    key={col.id}
                    content={isOpen ? optionDesc : null}
                    className="flex items-start justify-center"
                  >
                    <label
                      htmlFor={`row-${row.id}-${col.id}`}
                      className="flex h-full w-full flex-col items-center justify-center hover:bg-[#E4E3E3]"
                    >
                      <input
                        type="radio"
                        id={`row-${row.id}-${col.id}`}
                        name={`row-${row.id}`}
                        className="peer sr-only"
                        value={col.id}
                        checked={selectedValue === col.id}
                        onChange={() => handleSelectOption(row.id, col.id)}
                      />
                      <span
                        className={clsx(
                          "border-eerie-black relative inline-block h-[22px] w-[22px] flex-[0_0_22px] rounded-full border bg-white",
                          "after:bg-eerie-black after:absolute after:inset-[5px] after:rounded-full after:content-['']",
                          "after:scale-75 after:opacity-0 after:transition after:duration-150 after:ease-out",
                          "peer-checked:after:scale-100 peer-checked:after:opacity-100",
                          "peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
                        )}
                      />
                    </label>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
